import React, { useState, useEffect }  from 'react';
import FormNew from './FormNew';
import ProductImages from './ProductImages';
import { useProductImagesSelection } from './ProductImagesTree';
import SectionMenuLayout from 'components/quote_builder_widget/shared/SectionMenuLayout';
import { GetRequest, PostRequest } from 'components/common/index.js';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const LISTING_PATH = '/quote-builder/widgets';
const PRODUCT_IMAGES_PATH = '/quote-builder/widgets/product-images';

const List = ( { defaultUnit = 'mm', defaultLanguageOption = 'en', defaultCurrency = 'USD', widgetHost } ) => {
  const getInitialState = () => {
    const path = window.location.pathname;
    if (path === PRODUCT_IMAGES_PATH) {
      return { mode: 'product_images', identifier: '' };
    }
    const editMatch = path.match(/\/quote-builder\/widgets\/([^/]+)\/edit/);
    if (editMatch) {
      return { mode: 'edit', identifier: editMatch[1] };
    }
    if (path.includes('/quote-builder/widgets/new')) {
      return { mode: 'new', identifier: '' };
    }
    return { mode: 'listing', identifier: '' };
  };

  const initialState = getInitialState();
  const [mode, setMode] = useState(initialState.mode)
  const [widgets, setWidgets] = useState([])
  const [identifier, setIdentifier] = useState(initialState.identifier)
  const [widgetsCount, setWidgetsCount] = useState()
  const [productImagesCount, setProductImagesCount] = useState()
  const productImagesSelection = useProductImagesSelection()
  const isMenuMode = mode == 'listing' || mode == 'product_images'

  const widgetCard = (widget) => {
    return <div key={widget.identifier} className={styles.addedCard} data-testid="widget">
      <div className={styles.addedCardTitle}>{widget.identifier}</div>
      <div className={styles.addedCardDetails}>{t('quote_builder_widget.domains')}</div>
      <div className="attribute-properties">
        <div className="attribute-properties-row">
          { widget.domains.length > 0
            ? widget.domains.map((domain) => <span key={domain} className="attribute-tag">{domain}</span>)
            : <span className={styles.addedCardDetails}>{t('quote_builder_widget.installation_no_domains_short')}</span> }
        </div>
      </div>
      <div className={styles.addedCardActions}>
        <button type="button" className="dct-button dct-button--secondary dct-button--small" onClick={(e) => showEditForm(e, widget.identifier)}>
          <i className="fa fa-pencil" aria-hidden="true"></i>
          {t('quote_builder_widget.edit')}
        </button>
        <button type="button" className="dct-button dct-button--danger dct-button--small" onClick={(e) => deleteConfirm(e, widget.identifier)}>
          <i className="fa fa-trash" aria-hidden="true"></i>
          {t('quote_builder_widget.delete')}
        </button>
      </div>
    </div>
  }
  const listofItems = () => {
    return <div className={styles.widgetsListing}>
      <h4 className={styles.widgetsListingTitle}><span className="q-section-title">{t('quote_builder_widget.my_widgets')}</span></h4>
      { widgets.map(widgetCard) }
    </div>
  }

  const fetchWidgets = () => {
    if(!isMenuMode) { return }

    GetRequest({
      url: '/quote-builder/user-quote-builder-widgets',
      callback: (data) => {
        setWidgets( (_prev) => data );
        setWidgetsCount(data.length);
      }
    })
  }

  // The Product images page reports its own count; the Widgets page reads it for the menu.
  const fetchProductImagesCount = () => {
    if(mode != 'listing') { return }

    GetRequest({
      url: '/quote-builder/product-images/list',
      callback: (data) => {
        setProductImagesCount(data.images_count);
      }
    })
  }

  const showMenuSection = (section) => {
    const productImages = section == 'product_images';
    const path = productImages ? PRODUCT_IMAGES_PATH : LISTING_PATH;
    if (window.location.pathname != path) {
      window.history.pushState({}, '', path);
    }
    productImagesSelection.clearSelection();
    setMode(() => (productImages ? 'product_images' : 'listing'));
  }

  const activeMenuId = () => {
    if (mode != 'product_images') { return 'widgets' }

    return productImagesSelection.selection ? undefined : 'product_images'
  }

  const sectionMenu = (content) => (
    <SectionMenuLayout
      label={t('quote_builder_widget.widgets')}
      sections={[
        { id: 'widgets', label: 'quote_builder_widget.menu_widgets', count: widgetsCount },
        { id: 'product_images', label: 'quote_builder_widget.section_product_images', count: productImagesCount, subtree: mode == 'product_images' ? productImagesSelection.subtree : undefined },
      ]}
      activeId={activeMenuId()}
      onSectionChange={showMenuSection}
    >
      {content}
    </SectionMenuLayout>
  )

  const listofWidgets = () => {
    return (<>
      { widgets.length > 0 && listofItems() }

      { widgets.length == 0 && <div className="dct-empty-state">
        <i className="fa fa-code dct-empty-state__icon" aria-hidden="true"></i>
        <div className="dct-empty-state__action">
          <button type="button" onClick={showNewForm} className="dct-button dct-button--primary">
            <i className="fa fa-plus-square-o" aria-hidden="true"></i>
            {t('quote_builder_widget.new_widget')}
          </button>
        </div>
      </div> }
      </>)
  }

  const showEditForm = (e, widgetIdentifier) => {
    e.preventDefault();
    window.history.pushState({}, '', `/quote-builder/widgets/${widgetIdentifier}/edit`);
    setIdentifier(() => widgetIdentifier);
    setMode(() => 'edit');
  };

  const showNewForm = () => {
    window.history.pushState({}, '', '/quote-builder/widgets/new');
    setMode(() => 'new');
  };

  const goToListing = () => {
    window.history.pushState({}, '', LISTING_PATH);
    setMode(() => 'listing');
  }

  const deleteConfirm = (e, identifier) => {
    if(confirm( t('quote_builder_widget.delete_widget_confirm') )) {

    PostRequest({
      url: '/quote-builder/user-quote-builder-widgets-delete',
      data: {
        identifier: identifier,
      },
      callback: ( data ) => {
        setMode( () => 'listing' )
        setIdentifier( () => '' )
        fetchWidgets();
      },
      400: (data) => {
        setErrorMessage( () => data.error )
      }
    })

    }
  }

  const handlePopState = () => {
    const newState = getInitialState();
    setMode(newState.mode);
    setIdentifier(newState.identifier);
  };

  // useEffect
  useEffect(fetchWidgets, [mode]);
  useEffect(fetchProductImagesCount, [mode]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  // useEffect

  return (
    <div style={isMenuMode ? undefined : { marginLeft: '-20px' }}>
      {mode == 'listing' && sectionMenu(listofWidgets())}
      {mode == 'product_images' && sectionMenu(
        <ProductImages onCountChange={setProductImagesCount} onListChange={productImagesSelection.onListChange} selection={productImagesSelection.selection} />
      )}
      {mode == 'new' && <FormNew setMode={goToListing} defaultUnit={defaultUnit} defaultLanguageOption={defaultLanguageOption} defaultCurrency={defaultCurrency} widgetHost={widgetHost} /> }
      {mode == 'edit' && <FormNew identifier={identifier} setMode={goToListing} defaultUnit={defaultUnit} defaultLanguageOption={defaultLanguageOption} defaultCurrency={defaultCurrency} widgetHost={widgetHost} /> }
    </div>
  )
}

export default List;
